import type { AnalyticsClient, Logger } from '@/scopes/common/domain/types'
import type { CookieConsent } from '@/scopes/common/infrastructure/cookie-consent/types'
import type { useDependencies } from '@/scopes/common/infrastructure/dependencies'
import type { AccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import type { Price } from '@/scopes/prices/domain/prices/types'
import type { RoomInfo } from '@/scopes/prices/domain/rooms/types'
import { createSubmitEvent } from '@/scopes/prices/domain/analytics'
import { BookingDraftCreationError } from '@/scopes/prices/domain/BookingDraftCreationError'
import { PricesStoreApi } from '@/scopes/prices/infrastructure/store'

export function bookingDraftActions({
  bookingDraftApiClient,
  analyticsClient,
  store,
  logger,
  location = globalThis?.location,
}: {
  analyticsClient: AnalyticsClient
  bookingDraftApiClient: ReturnType<typeof useDependencies>['bookingDraftApiClient']
  store: PricesStoreApi
  logger: Logger
  location?: Location
}) {
  async function bookClickHandler({
    prices,
    selectedRooms,
    accommodationContext,
    url,
    isFlightOnly,
    cookieConsent,
    nextStepUrl = '/vakantie/bookingpage/boeking-stap-1',
  }: {
    prices: Price[]
    selectedRooms: RoomInfo[]
    accommodationContext: AccommodationContext
    url: string
    isFlightOnly: boolean
    cookieConsent: CookieConsent
    nextStepUrl?: string
  }) {
    const { contextItemId, accoId } = accommodationContext
    const { setIn, filterSelection, selectedPriceId, selectedInboundRouteId, selectedOutboundRouteId, selectedOutboundBusId, selectedInboundBusId } = store.getState()
    const filtersCriteria = {
      contextItemId,
      accoId,
      filterSelection,
      isFlightOnly,
      selectedPriceId: selectedPriceId || '',
      selectedRooms,
      url,
      cookieConsent,
      ...(selectedOutboundRouteId && { outboundFlight: selectedOutboundRouteId }),
      ...(selectedInboundRouteId && { inboundFlight: selectedInboundRouteId }),
      ...(selectedOutboundBusId && { outboundBus: selectedOutboundBusId }),
      ...(selectedInboundBusId && { inboundBus: selectedInboundBusId }),
    }

    setIn('createBookingDraft.loading', true)
    let data

    try {
      data = await bookingDraftApiClient.createBookingDraft(filtersCriteria)
    }
    catch (e) {
      logger.logError(new BookingDraftCreationError(e))
      setIn('createBookingDraft.error', true)
      setIn('createBookingDraft.loading', false)
      return
    }

    setIn('createBookingDraft', {
      loading: false,
      error: false,
      success: true,
    })
    const newUrl = `${location.origin}${nextStepUrl}?sid=${data.draftId}`
    sendSubmitEvent({ prices, selectedRooms, store, analyticsClient })
    location.replace(newUrl)
  }

  function resetState() {
    const state = store.getState()
    state.setIn('createBookingDraft', {
      loading: false,
      error: false,
      success: false,
    })
  }

  return { bookClickHandler, resetState }
}

function sendSubmitEvent({
  prices,
  selectedRooms,
  store,
  analyticsClient,
}: {
  prices: Price[]
  selectedRooms: RoomInfo[]
  store: PricesStoreApi
  analyticsClient: AnalyticsClient
}) {
  const { analytics, filterSelection, selectedPriceId } = store.getState()
  if (!analytics)
    return
  analyticsClient.sendEvent(
    createSubmitEvent({
      selectedRooms,
      analytics,
      filterSelection,
      selectedPrice: prices.find(({ id: priceId }) => priceId === selectedPriceId),
    }),
  )
}
